import { Box } from "@gemini/core"
import { EnvelopeIcon, PaperPlaneIcon, PhoneIcon } from "@gemini/icons"
import { Dropdown, FeedbackMessage, Modal, TextArea, TextField } from "@gemini/ui"
import { ComponentType, useEffect, useRef, useState } from "react"
import { Controller } from "react-hook-form"
import { useTranslation } from "react-i18next"

import { ContactSalutationType, ContactType, FormKey, germanBrands } from "@aviv-ipp/bff-types"

import { LinkNewTab } from "../../components/Link/LinkNewTab"
import { useIppContext } from "../../context/IppContext"
import { useParamsContactForm } from "../../hooks/useParamsContactForm"
import { useConstants } from "./useConstants"
import { useContactFormValidation } from "./useContactFormValidation"

const Dummy = () => <></>
const ContactForm = () => {
  const { t } = useTranslation()
  const { brand } = useIppContext()
  const isGermanBrand = germanBrands.includes(brand as (typeof germanBrands)[number])
  const {
    isOpen,
    setIsOpen,
    isLoading,
    isError,
    setIsError,
    reset,
    title,
    setContactType,
    contactTypeSelected,
    control,
    trigger,
    setValue,
    handleSubmit: handleSubmitForm,
    firstInputError,
    customPlaceholder,
  } = useContactFormValidation()
  const formRef = useRef<HTMLFormElement | null>(null)
  const errorRef = useRef<HTMLDivElement | null>(null)
  const inputRefs = useRef<Record<string, HTMLElement | null>>({})
  const setRef = (name: FormKey) => (el: HTMLElement | null) => (inputRefs.current[name] = el)
  const [submitting, setSubmitting] = useState(false)

  const { contactTypePlaceholders, dropdownContactTypeItems, dropdownContactSalutationItems } =
    useConstants(isGermanBrand)

  function setRequestMessage() {
    if (!customPlaceholder) {
      setValue(
        "contactRequest.message",
        t(contactTypePlaceholders[contactTypeSelected as ContactType])
      )
    } else {
      setValue("contactRequest.message", customPlaceholder)
    }
  }

  useEffect(() => {
    setRequestMessage()
  }, [contactTypeSelected])

  useEffect(() => {
    if (isOpen) setRequestMessage()
  }, [isOpen])

  useParamsContactForm()

  useEffect(() => {
    if (isError) {
      scrollToRef(errorRef.current)
    }
  }, [isError])

  useEffect(() => {
    if (!submitting) return
    if (firstInputError) {
      scrollToRef(inputRefs.current[firstInputError as string])
      return
    }
    // if form has not been touched but submitted and if user has scrolled down
    // firstInputError is undefined because not reactive, so we scroll to the form
    else if (!firstInputError) {
      scrollToRef(formRef.current)
      return
    }
    handleSubmitForm()
  }, [submitting])

  // scrolling should be always called inside a setTimeout to be invoked at the end of the call stack
  const scrollToRef = (ref: HTMLElement | null) => {
    const timeout = setTimeout(() => {
      ref?.scrollIntoView({ behavior: "smooth" })
      clearTimeout(timeout)
    })
  }

  const handleSubmit = () => {
    setSubmitting(true)
    trigger().then(() => {
      handleSubmitForm()
      setSubmitting(false)
    })
  }

  return (
    <Modal
      isOpen={isOpen}
      title={title}
      onOpenChange={(isOpen) => {
        setIsOpen(isOpen)
        reset(undefined, { keepDefaultValues: true })
        setContactType(undefined)
        setIsError(false) // do not show error if the modal has been closed and reopened
      }}
      trigger={Dummy}
      displayMode="default"
      footerEndButtonProps={{
        children: t("contactAgency_sendMessage"),
        icon: PaperPlaneIcon,
        onPress: () => handleSubmit(),
        isLoading,
      }}
      testId="contact-form"
    >
      {isError && (
        <Box ref={errorRef} testId="contact-form-error">
          <FeedbackMessage
            variant="error"
            title={t("contactAgency_sendMessageErrorTitle")}
            marginBottom="spacing.16"
          >
            {t("contactAgency_sendMessageErrorText")}
          </FeedbackMessage>
        </Box>
      )}
      <Box as="form" ref={formRef}>
        {isGermanBrand && (
          <Box marginBottom="spacing.16" ref={setRef("salutation")}>
            <Controller
              name="contactRequest.personContact.salutation"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <Dropdown
                  {...field}
                  {...(error && {
                    error: t(error.message as string),
                  })}
                  testId="contact-salutation-type-dropdown"
                  label={t("contactAgency_projectOwnerSalutation")}
                  defaultItems={dropdownContactSalutationItems}
                  onChange={(newSalutation) => {
                    setValue(
                      "contactRequest.personContact.salutation",
                      newSalutation as ContactSalutationType
                    )
                    trigger("contactRequest.personContact.salutation")
                  }}
                />
              )}
            />
          </Box>
        )}
        <Box
          display="flex"
          flexWrap={{ "breakpoint.base": "wrap", "breakpoint.sm": "nowrap" }}
          marginBottom="spacing.16"
          gap="spacing.16"
        >
          <Box
            flexBasis={{ "breakpoint.base": "100%", "breakpoint.sm": "50%" }}
            ref={setRef("firstName")}
          >
            <Controller
              name="contactRequest.personContact.firstName"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  {...(error && {
                    error: t(error.message as string),
                  })}
                  onBlur={() => trigger("contactRequest.personContact.firstName")}
                  label={t("contactAgency_projectOwnerFirstName")}
                  type="text"
                  placeholder={t("contactAgency_projectOwnerNamePlaceholder")}
                  isFullWidth
                />
              )}
            />
          </Box>
          <Box
            flexBasis={{ "breakpoint.base": "100%", "breakpoint.sm": "50%" }}
            ref={setRef("lastName")}
          >
            <Controller
              name="contactRequest.personContact.lastName"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  {...(error && {
                    error: t(error.message as string),
                  })}
                  onBlur={() => trigger("contactRequest.personContact.lastName")}
                  label={t("contactAgency_projectOwnerLastName")}
                  type="text"
                  placeholder={t("contactAgency_projectOwnerLastNamePlaceholder")}
                  isFullWidth
                />
              )}
            />
          </Box>
        </Box>
        <Box marginBottom="spacing.16" ref={setRef("phone")}>
          <Controller
            name="contactRequest.personContact.phone"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                isOptional={isGermanBrand}
                {...field}
                {...(error && {
                  error: t(error.message as string),
                })}
                onBlur={() => trigger("contactRequest.personContact.phone")}
                label={t("contactAgency_projectOwnerPhone")}
                type="tel"
                iconStart={PhoneIcon}
                placeholder={undefined} // decision to keep it empty for now, might be revisited later (+ Gemini phone input)
              />
            )}
          />
        </Box>
        <Box marginBottom="spacing.16" ref={setRef("email")}>
          <Controller
            name="contactRequest.personContact.email"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                {...(error && {
                  error: t(error.message as string),
                })}
                onBlur={() => trigger("contactRequest.personContact.email")}
                label={t("contactAgency_projectOwnerEmail")}
                type="email"
                iconStart={EnvelopeIcon}
                placeholder={t("contactAgency_projectOwnerEmailPlaceholder")}
              />
            )}
          />
        </Box>
        {!customPlaceholder && (
          <Box marginBottom="spacing.16">
            <Dropdown
              testId="contact-type-dropdown"
              label={t("contactAgency_projectTypes")}
              defaultItems={dropdownContactTypeItems}
              defaultValue={contactTypeSelected}
              onChange={async (newContactType) => {
                setContactType(newContactType as ContactType)
                setValue(
                  "contactRequest.message",
                  t(contactTypePlaceholders[newContactType as ContactType])
                )
                await trigger("contactRequest.message")
              }}
            />
          </Box>
        )}
        <Box ref={setRef("message")}>
          <Controller
            name="contactRequest.message"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextArea
                {...field}
                {...(error && {
                  error: t(error.message as string),
                })}
                //@ts-ignore
                onBlur={() => trigger("contactRequest.message")}
                label={t("contactAgency_messageTitle")}
                maxCharacters={500}
                rows={4}
              />
            )}
          />
        </Box>
      </Box>

      <Box as="p" typography="typography.body.14.regular" marginTop="spacing.16">
        {t("contactAgency_submitTermsConditions.0")}{" "}
        <LinkNewTab href={t("tempFooter_terms_url")} variant="inline">
          {t("terms")}
        </LinkNewTab>{" "}
        {t("contactAgency_submitTermsConditions.1")}{" "}
        {t("contactAgency_submitTermsConditions.2", { platform: brand })}{" "}
        <LinkNewTab href="https://www.aviv-group.com/companies" variant="inline">
          AVIV Group
        </LinkNewTab>{" "}
        {t("contactAgency_submitTermsConditions.3")}{" "}
        <LinkNewTab href={t("tempFooter_privacy_url")} variant="inline">
          {t("privacy")}
        </LinkNewTab>
        .
      </Box>
    </Modal>
  )
}

export default ContactForm as ComponentType
